import { Component, ElementRef, EventEmitter, Output, ViewChild, HostListener, computed, signal, input, model } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ItemDataService } from "../../services/item-data.service";
import { ValueComponent } from "../value/value.component";
import { TooltipWrapperComponent } from "../tooltip-wrapper/tooltip-wrapper.component";
import { BaseComponent } from "../base.component";
import { EconomyService } from "../../services/economy.service";
import { PricedItem } from "../../models/economy-models";
import { GearTierType, JewelleryTierType } from "../../models/item-data-models";
import { GearTierIconComponent } from "../icons/gear-tier-icon/gear-tier-icon.component";
import { JewelleryTierIconComponent } from "../icons/jewellery-tier-icon/jewellery-tier-icon.component";

export type AutocompleteOption = { name: string; image?: string; gearTier?: GearTierType; jewelleryTier?: JewelleryTierType };

@Component({
  standalone: true,
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrl: './autocomplete.component.scss',
  imports: [FormsModule, ValueComponent, TooltipWrapperComponent, GearTierIconComponent, JewelleryTierIconComponent]
})
export class AutocompleteComponent extends BaseComponent {
  allLabel = input<string>('All');
  allowAll = input<boolean>(true);
  allowNone = input<boolean>(false);
  options = input.required<AutocompleteOption[]>();
  showOptions = input.required<boolean>();
  disabled = input<boolean>(false);
  disabledPlaceholder = input<string | undefined>(undefined);
  selectedValues = model.required<string[]>();
  showTooltip = input<boolean>(true);

  @Output() selectedValuesChange = new EventEmitter<string[]>();

  @ViewChild('autoCompleteInputElement') inputElement!: ElementRef;

  open = false;
  searchText = signal('');
  extraInfo?: string[] = undefined;
  value?: PricedItem = undefined;

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (this.open && !this.elementRef.nativeElement.contains(event.target)) {
      this.open = false;
    }
  }

  constructor(private elementRef: ElementRef, private itemDataService: ItemDataService, private economyService: EconomyService) {
    super();
  }

  onSelectedValuesChange(newValues: string[]) {
    this.selectedValues.set(newValues);
    this.selectedValuesChange.emit(newValues);
  }

  openAndSetFocus() {
    if (this.disabled()) return;
    this.open = true;
    setTimeout(() => {
      if (this.inputElement) {
        this.inputElement.nativeElement.focus();
      }
    }, 1);
  }

  isSelected(option: string) {
    return this.selectedValues().includes(option);
  }

  toggle(option: string) {
    let newValues = this.selectedValues();
    if (this.isSelected(option)) {
      newValues = newValues.filter(v => v !== option);
      if (newValues.length === 0) {
        newValues = this.allowNone() ? [] : [this.allowAll() ? this.allLabel() : option];
      }
    } else {
      if (option === this.allLabel()) {
        newValues = [this.allLabel()];
      } else {
        if (option != this.allLabel() && newValues.includes(this.allLabel())) {
          newValues = [option];
        } else {
          newValues = [...newValues, option];
        }
      }
    }
    this.onSelectedValuesChange(newValues);
    if (!this.settings.autocompleteRetainSearch()) {
      this.searchText.set('');
    }
    this.inputElement.nativeElement.focus();
  }

  close() {
    this.open = false;
    this.searchText.set('');
  }

  hovered(baseType: string) {
    const currencyData = this.itemDataService.getCurrency(baseType);
    this.extraInfo = currencyData?.effects;
    if (!this.extraInfo && currencyData?.description) {
      this.extraInfo = [currencyData.description];
    }
    if (!this.extraInfo) {
      this.extraInfo = this.itemDataService.getJewellery(baseType)?.implicits;
    }
    if (!this.extraInfo) {
      this.extraInfo = this.itemDataService.getFlask(baseType)?.effects;
    }

    this.value = this.economyService.getCurrencyValue(baseType);
  }

  unhovered() {
    this.extraInfo = undefined;
    this.value = undefined;
  }

  filteredOptions = computed(() => {
    const searchTextValue = this.searchText();
    if (!searchTextValue) return this.options();
    return this.options().filter(o => o.name.toLowerCase().includes(searchTextValue.toLowerCase()));
  });

  selectedOptions = computed(() => {
    if (!this.selectedValues()) return [];
    if (!this.options()) return [];
    return this.selectedValues().map(v => v === this.allLabel() ? { name: this.allLabel() } : (this.options().find(o => o.name === v) || { name: v }));
  });

  formattedSelectedValues = computed(() => {
    return this.selectedValues().join(', ');
  });
}
